// ─────────────────────────────────────────────────────────────────────────
// calc.ts — pure, framework-agnostic solar payback maths (brief v1 §7).
// No DOM, no config import: every threshold/rate is passed in, which is what
// makes the package white-label. Unit-tested in test/calc.test.ts.
//
// Battery economics are computed SEPARATELY and never blended into the solar
// payback (a hard non-negotiable). No figure is presented as guaranteed.
// ─────────────────────────────────────────────────────────────────────────

import type {
  BatteryMarginal,
  BillPeriod,
  CalcResult,
  ConsumptionSplit,
  LifetimeYear,
  Scenario,
  SensitivityRange,
  StcZone,
  SystemResult,
  UsageProfile,
} from './types.js';

const PERIODS_PER_YEAR: Record<BillPeriod, number> = {
  weekly: 52,
  monthly: 12,
  quarterly: 4,
  annual: 1,
};

/**
 * Default share of total household consumption that occurs in daylight, when
 * solar can serve it directly. The complement is evening/overnight load that
 * ONLY a battery can shift (by storing daytime surplus). Benchmark-aligned
 * (Solargain day-use %, SolarQuotes); config can override per client.
 */
export const DEFAULT_DAYTIME_LOAD_SHARES: Record<UsageProfile, number> = {
  'mostly-daytime': 0.65,
  mixed: 0.45,
  'mostly-evenings': 0.3,
  'pool-or-ev': 0.5,
};

const round = (n: number, dp = 0): number => {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
};

const clamp = (n: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, n));

/** Annualise a bill given its period. */
export function annualiseBill(bill: number, period: BillPeriod): number {
  return bill * PERIODS_PER_YEAR[period];
}

/**
 * Annual consumption (kWh) implied by a bill:
 *   (annual bill − annual supply charge) ÷ retail rate.
 * Supply charge is the fixed daily component, which solar does not offset.
 */
export function estimateAnnualUsageKwh(
  annualBill: number,
  supplyChargePerDay: number,
  retailRate: number,
): number {
  if (retailRate <= 0) return 0;
  const annualSupply = supplyChargePerDay * 365;
  const energySpend = annualBill - annualSupply;
  return Math.max(0, energySpend / retailRate);
}

/**
 * Annual generation (kWh). The CER zone rating IS the deemed annual kWh per kW
 * (zone rating ÷ 365 ≈ effective peak-sun-hours); we then apply a conservative
 * performance-ratio derate plus orientation and shade factors.
 */
export function estimateGenerationKwh(
  sizeKw: number,
  zoneRatingKwhPerKw: number,
  performanceRatio: number,
  orientationFactor: number,
  shadeFactor: number,
): number {
  return (
    sizeKw *
    zoneRatingKwhPerKw *
    performanceRatio *
    orientationFactor *
    shadeFactor
  );
}

/**
 * Split generation into self-consumed vs exported.
 *
 * The panels serve the household's DAYTIME load directly (up to what they
 * generate); anything beyond that is surplus. Without a battery the surplus is
 * exported. A battery stores surplus to serve the EVENING load — so its value
 * scales with the evening demand it can displace, not with leftover daytime
 * demand (which a well-sized array has already met). Energy is conserved:
 * selfKwh + exportKwh = genKwh.
 */
export function splitSelfConsumption(
  genKwh: number,
  annualUsageKwh: number,
  daytimeShare: number,
  battery: { usableKwh: number; roundTripEfficiency: number } | null,
): ConsumptionSplit {
  const daytimeLoad = annualUsageKwh * daytimeShare;
  const eveningLoad = annualUsageKwh - daytimeLoad;

  // Panels serve the daytime load directly, capped by what they generate.
  let selfKwh = Math.min(genKwh, daytimeLoad);
  let surplus = Math.max(0, genKwh - selfKwh); // would export without a battery

  if (battery && battery.usableKwh > 0) {
    const throughput = battery.usableKwh * 365 * battery.roundTripEfficiency;
    const stored = Math.min(throughput, surplus, eveningLoad);
    selfKwh += stored;
    surplus -= stored;
  }

  return {
    selfKwh,
    exportKwh: Math.max(0, surplus),
    selfFraction: genKwh > 0 ? selfKwh / genKwh : 0,
  };
}

/**
 * Year-one savings: self-consumed valued at the retail rate, exports at the FiT.
 *
 * GST (brief §10a gap D): the retail rate is GST-INCLUSIVE (it's what a household
 * pays per kWh on its bill), so avoided consumption is correctly valued tax-in.
 * Residential feed-in credits are GST-FREE, so the FiT is applied as-is. No GST
 * adjustment is therefore needed here — the inputs already carry the right basis.
 */
export function annualSavings(
  selfKwh: number,
  exportKwh: number,
  retailRate: number,
  feedInTariff: number,
): number {
  return selfKwh * retailRate + exportKwh * feedInTariff;
}

/** Simple payback (years), or null if year-one savings are zero/negative. */
export function simplePaybackYears(
  netCost: number,
  year1Savings: number,
): number | null {
  if (year1Savings <= 0) return null;
  return netCost / year1Savings;
}

/**
 * STC dollar value: number of certificates = capacity × zone rating (MWh/kW) ×
 * deeming years, floored, times the STC price. (zone rating kWh/kW ÷ 1000 = MWh/kW.)
 */
export function estimateStcValue(
  sizeKw: number,
  zoneRatingKwhPerKw: number,
  deemingYears: number,
  stcPrice: number,
): number {
  const ratingMwhPerKw = zoneRatingKwhPerKw / 1000;
  const stcCount = Math.floor(sizeKw * ratingMwhPerKw * deemingYears);
  return Math.max(0, stcCount) * stcPrice;
}

/**
 * Federal battery discount (Cheaper Home Batteries Program), shown separately.
 * STCs = usable kWh × deeming, but the STC factor TAPERS across capacity bands
 * (e.g. 100% to 14 kWh, 60% to 28, 15% to 50; nothing above 50). The deemed STC
 * count is floored, then valued at the STC price.
 */
export function estimateBatteryRebate(
  usableKwh: number,
  deemingPerKwh: number,
  stcPrice: number,
  taperBands: ReadonlyArray<{ upToKwh: number; factor: number }>,
): number {
  let prev = 0;
  let effectiveKwh = 0;
  for (const band of taperBands) {
    const inBand = Math.max(0, Math.min(usableKwh, band.upToKwh) - prev);
    effectiveKwh += inBand * band.factor;
    prev = band.upToKwh;
    if (usableKwh <= band.upToKwh) break;
  }
  const stcCount = Math.floor(effectiveKwh * deemingPerKwh);
  return Math.max(0, stcCount) * stcPrice;
}

/**
 * Snap a recommended size to the standard AU array sizes from estimated usage.
 * `sizesKw` and the per-kW yield are passed in (config-driven), so low-usage
 * homes can land below 6.6 kW rather than always defaulting to it.
 */
export function recommendSystemSize(
  annualUsageKwh: number,
  profile: UsageProfile,
  genPerKwYr = 1300,
  sizesKw: readonly number[] = [5, 6.6, 8, 10, 13.2],
): number {
  // Daytime-heavy homes self-consume more, so size up toward usage; evening-heavy
  // homes get less value from a big array, so size nearer their daytime slice.
  const daytimeBias =
    profile === 'mostly-daytime'
      ? 1.15
      : profile === 'pool-or-ev'
        ? 1.1
        : profile === 'mixed'
          ? 1.0
          : 0.9;
  const yield_ = genPerKwYr > 0 ? genPerKwYr : 1300;
  const targetKw = (annualUsageKwh * daytimeBias) / yield_;
  for (const s of sizesKw) {
    if (targetKw <= s) return s;
  }
  return sizesKw[sizesKw.length - 1] as number;
}

/**
 * Investment metrics over the project cashflows (brief §10a gap B).
 * ROI% = lifetime net ÷ net cost; NPV discounts each year's saving; IRR is the
 * discount rate that zeroes NPV (bisection — pure, no dependencies).
 */
export function investmentReturns(
  netCost: number,
  annualSavingsByYear: number[],
  discountRate: number,
): { roiPercent: number; irr: number | null; npv: number } {
  const totalSavings = annualSavingsByYear.reduce((a, b) => a + b, 0);
  const roiPercent = netCost > 0 ? ((totalSavings - netCost) / netCost) * 100 : 0;

  const npvAt = (r: number): number =>
    annualSavingsByYear.reduce(
      (acc, s, i) => acc + s / (1 + r) ** (i + 1),
      -netCost,
    );
  const npv = npvAt(discountRate);

  // IRR via bisection over a sane bracket; null if it never breaks even.
  let irr: number | null = null;
  if (totalSavings > netCost && netCost > 0) {
    let lo = -0.9;
    let hi = 1.5;
    let fLo = npvAt(lo);
    if (fLo * npvAt(hi) <= 0) {
      for (let i = 0; i < 100; i++) {
        const mid = (lo + hi) / 2;
        const fMid = npvAt(mid);
        irr = mid;
        if (Math.abs(fMid) < 1e-6) break;
        if (fLo * fMid < 0) {
          hi = mid;
        } else {
          lo = mid;
          fLo = fMid;
        }
      }
    }
  }
  return { roiPercent, irr, npv };
}

/**
 * Year-by-year projection with panel degradation, electricity inflation and a
 * declining feed-in tariff (with a floor). Returns the per-year series plus the
 * 10- and 25-year cumulative net position.
 */
export function projectLifetime(
  scenario: Scenario,
  netCost: number,
  annualUsageKwh: number,
  baseGenKwh: number,
  battery: { usableKwh: number; roundTripEfficiency: number } | null,
  years = 25,
): { lifetime: LifetimeYear[]; net10: number; net25: number } {
  const lifetime: LifetimeYear[] = [];
  let cumulative = -netCost;
  let net10 = -netCost;
  let net25 = -netCost;
  const gstFactor = scenario.gstRegistered ? 1 / 1.1 : 1; // ex-GST for businesses

  for (let y = 1; y <= years; y++) {
    // Disclosed curve: a first-year (LID) step then a gentler annual decline,
    // e.g. 97% in year 1 → ~82% by year 25 (Solar Choice benchmark).
    const degradeFactor =
      (1 - scenario.firstYearDegradation) * (1 - scenario.degradation) ** (y - 1);
    const genKwh = baseGenKwh * degradeFactor;
    const retail =
      scenario.retailRate * gstFactor * (1 + scenario.electricityInflation) ** (y - 1);
    const fit = Math.max(
      scenario.fitFloor,
      scenario.feedInTariff * (1 - scenario.fitDecline) ** (y - 1),
    );
    const split = splitSelfConsumption(
      genKwh,
      annualUsageKwh,
      scenario.daytimeLoadShare,
      battery,
    );
    const savings = annualSavings(split.selfKwh, split.exportKwh, retail, fit);
    cumulative += savings;
    lifetime.push({
      year: y,
      generationKwh: round(genKwh),
      savings: round(savings, 2),
      cumulativeNet: round(cumulative, 2),
    });
    if (y === 10) net10 = cumulative;
    if (y === 25) net25 = cumulative;
  }
  return { lifetime, net10: round(net10, 2), net25: round(net25, 2) };
}

/** Battery economics shown separately: extra saving, extra upfront, marginal payback. */
export function batteryMarginal(
  solarOnly: SystemResult,
  solarPlusBattery: SystemResult,
): BatteryMarginal {
  const extraAnnualSaving =
    solarPlusBattery.year1Savings - solarOnly.year1Savings;
  const extraUpfront = solarPlusBattery.netCost - solarOnly.netCost;
  return {
    extraAnnualSaving: round(extraAnnualSaving, 2),
    extraUpfront: round(extraUpfront, 2),
    marginalPaybackYears:
      extraAnnualSaving > 0 ? round(extraUpfront / extraAnnualSaving, 1) : null,
  };
}

/** Lead score (0–100): bigger bills, battery interest, ownership and a strong payback all lift it. */
export function leadScore(
  scenario: Scenario,
  result: SystemResult,
  batteryInterest: boolean,
): number {
  let score = 0;
  const annualBill = annualiseBill(scenario.bill, scenario.billPeriod);
  // Bill size (up to 40).
  score += clamp(annualBill / 60, 0, 40);
  // Payback strength (up to 30): a 4-year payback maxes, 12+ scores ~0.
  if (result.paybackYears != null) {
    score += clamp(30 - (result.paybackYears - 4) * 3.75, 0, 30);
  }
  // Battery interest (15) and home ownership proxy via no existing solar (15).
  if (batteryInterest) score += 15;
  if (!scenario.hasExistingSolar) score += 15;
  return Math.round(clamp(score, 0, 100));
}

// ── orchestrator ────────────────────────────────────────────────────────────

function buildSystemResult(
  scenario: Scenario,
  annualUsageKwh: number,
  withBattery: boolean,
): SystemResult {
  const battery =
    withBattery && scenario.battery
      ? {
          usableKwh: scenario.battery.usableKwh,
          roundTripEfficiency: scenario.battery.roundTripEfficiency,
        }
      : null;

  const annualGenerationKwh = estimateGenerationKwh(
    scenario.sizeKw,
    scenario.zoneRatingKwhPerKw,
    scenario.performanceRatio,
    scenario.orientationFactor,
    scenario.shadeFactor,
  );

  const split = splitSelfConsumption(
    annualGenerationKwh,
    annualUsageKwh,
    scenario.daytimeLoadShare,
    battery,
  );

  const stcValue = estimateStcValue(
    scenario.sizeKw,
    scenario.zoneRatingKwhPerKw,
    scenario.deemingYears,
    scenario.stcPrice,
  );

  // GST: a GST-registered business reclaims input credits, so cost and the value
  // of avoided consumption are counted EX-GST. Usage (kWh) is GST-invariant, so
  // it is derived from the inc-GST bill upstream and untouched here. Exports
  // (FiT) are GST-free regardless. Residential → gstFactor 1.
  const gstFactor = scenario.gstRegistered ? 1 / 1.1 : 1;

  const solarGross = scenario.sizeKw * scenario.costPerKw * gstFactor;
  const batteryGross =
    withBattery && scenario.battery
      ? scenario.battery.usableKwh * scenario.battery.costPerKwh * gstFactor
      : 0;
  const batteryRebate =
    withBattery && scenario.battery ? scenario.battery.rebate : 0;

  const grossCost = solarGross + batteryGross;
  const netCost = Math.max(0, grossCost - stcValue - batteryRebate);

  const year1Savings = annualSavings(
    split.selfKwh,
    split.exportKwh,
    scenario.retailRate * gstFactor,
    scenario.feedInTariff,
  );

  const annualBill = annualiseBill(scenario.bill, scenario.billPeriod) * gstFactor;
  const newAnnualBill = Math.max(
    scenario.supplyChargePerDay * 365 * gstFactor,
    annualBill - year1Savings,
  );

  const { lifetime, net10, net25 } = projectLifetime(
    scenario,
    netCost,
    annualUsageKwh,
    annualGenerationKwh,
    battery,
  );

  const returns = investmentReturns(
    netCost,
    lifetime.map((l) => l.savings),
    scenario.discountRate,
  );
  const panelCount =
    scenario.panelWatts > 0
      ? Math.round((scenario.sizeKw * 1000) / scenario.panelWatts)
      : 0;

  return {
    sizeKw: scenario.sizeKw,
    grossCost: round(grossCost, 2),
    stcValue: round(stcValue, 2),
    batteryRebate: round(batteryRebate, 2),
    netCost: round(netCost, 2),
    annualGenerationKwh: round(annualGenerationKwh),
    annualUsageKwh: round(annualUsageKwh),
    split: {
      selfKwh: round(split.selfKwh),
      exportKwh: round(split.exportKwh),
      selfFraction: round(split.selfFraction, 3),
    },
    year1Savings: round(year1Savings, 2),
    newAnnualBill: round(newAnnualBill, 2),
    paybackYears: (() => {
      const p = simplePaybackYears(netCost, year1Savings);
      return p == null ? null : round(p, 1);
    })(),
    cumulativeNet10: net10,
    cumulativeNet25: net25,
    emissionsAvoidedKgPerYear: round(
      annualGenerationKwh * scenario.emissionsFactorKgPerKwh,
    ),
    panelCount,
    returns: {
      roiPercent: round(returns.roiPercent),
      irr: returns.irr == null ? null : round(returns.irr * 100, 1),
      npv: round(returns.npv),
    },
    lifetime,
  };
}

function sensitivity(scenario: Scenario, annualUsageKwh: number): SensitivityRange {
  // Vary generation ±10% (orientation/shade/weather) to bracket payback.
  const make = (gen: number, infl: number): number => {
    const s: Scenario = {
      ...scenario,
      performanceRatio: scenario.performanceRatio * gen,
      electricityInflation: infl,
    };
    const r = buildSystemResult(s, annualUsageKwh, false);
    return r.paybackYears ?? 0;
  };
  return {
    conservative: make(0.9, Math.max(0, scenario.electricityInflation - 0.01)),
    expected: make(1.0, scenario.electricityInflation),
    optimistic: make(1.1, scenario.electricityInflation + 0.01),
  };
}

/**
 * Top-level entry: resolve a scenario into the full result set — solar-only,
 * optional solar+battery, the separate battery-marginal block, a payback
 * sensitivity band, a recommended size and a lead score.
 */
export function calc(scenario: Scenario, configVersion: string): CalcResult {
  const annualBill = annualiseBill(scenario.bill, scenario.billPeriod);
  const annualUsageKwh = estimateAnnualUsageKwh(
    annualBill,
    scenario.supplyChargePerDay,
    scenario.retailRate,
  );

  const solar = buildSystemResult(scenario, annualUsageKwh, false);
  const solarPlusBattery = scenario.battery
    ? buildSystemResult(scenario, annualUsageKwh, true)
    : null;

  // Worst-case: feed-in tariff = 0c (brief §10a gap C). Exports earn nothing,
  // so only self-consumption pays back.
  const zeroFit = buildSystemResult(
    { ...scenario, feedInTariff: 0, fitFloor: 0, battery: null },
    annualUsageKwh,
    false,
  );

  // Effective annual yield per kW after PR (zone rating × performance ratio),
  // so a low-usage home can be sized below 6.6 kW rather than defaulting to it.
  const genPerKwYr =
    scenario.zoneRatingKwhPerKw * scenario.performanceRatio || 1300;

  return {
    solar,
    solarPlusBattery,
    batteryMarginal: solarPlusBattery
      ? batteryMarginal(solar, solarPlusBattery)
      : null,
    paybackSensitivity: sensitivity(scenario, annualUsageKwh),
    zeroFitPaybackYears: zeroFit.paybackYears,
    recommendedSizeKw: recommendSystemSize(
      annualUsageKwh,
      scenario.usageProfile,
      genPerKwYr,
      scenario.systemSizesKw,
    ),
    // Generation materially exceeds usage → flag a "larger than your usage" note.
    oversizedForUsage: solar.annualGenerationKwh > annualUsageKwh * 1.6,
    leadScore: leadScore(scenario, solar, scenario.battery != null),
    configVersion,
  };
}

/** STC deeming years for an install year: SRES winds down to the end of 2030. */
export function deemingYearsFor(installYear: number, sresEndYear = 2030): number {
  return clamp(sresEndYear - installYear + 1, 1, 15);
}

export type { StcZone };
